import type { FunctionComponent } from 'preact';
import { useContext, useMemo } from 'preact/hooks';

import type { TemplateFunction } from 'Components/types';
import { removalFromGarageRequested, vehicleSelected } from 'Core/actions/fitmentSearch/index';
import {
  createVehicleIsSelectedSelector,
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import type { Vehicle } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import { cloneSafe } from 'Utils/components';
import { simpleHandler } from 'Utils/roleHandler';

import type { GarageVehicleTemplateParams } from '../../types';
import GarageContext from '../garageContext';

type Props = {
  template: TemplateFunction<GarageVehicleTemplateParams>;
  vehicle: Vehicle;
};

const GarageItem: FunctionComponent<Props> = ({ template, vehicle }) => {
  const dispatch = useDispatch();

  const { closeGarage, doNotRedirectOnVehicleSelect, redirectUrl } = useContext(GarageContext);

  const isSelectedSelector = useMemo(() => createVehicleIsSelectedSelector(vehicle), [vehicle]);

  const isSelected = useSelector(isSelectedSelector);
  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector);

  const removeVehicle = () => {
    dispatch(removalFromGarageRequested(vehicle, isSelected));
    fitmentSearchConfig.onVehicleDiscarded();
  };

  const selectVehicle = () => {
    closeGarage?.();

    dispatch(
      vehicleSelected(vehicle, {
        doNotRedirectOnVehicleSelect,
        isSelectedFromGarage: true,
        redirectUrl,
        sendIsolatedRequest: true,
      }),
    );

    fitmentSearchConfig.onVehicleSelected(vehicle.filteredFieldMap);
  };

  const onClick = simpleHandler({ removeVehicle, selectVehicle });

  const component = template.call({
    isSelected,
    vehicleString: vehicle.toString(),
    vehicle: vehicle.filteredFieldMap,
    vehicleBaseValues: vehicle.getFilteredValues(baseFitmentFields),
    vehicleExtraValues: vehicle.getFilteredValues(extraFitmentFields),
    vehicleLogo: vehicle.logo,
  });
  return cloneSafe(component, null, { onClick });
};

export default GarageItem;
